import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styles, colors } from '../styles/styles';
import { formatTimestamp } from '../utils/dataProcessing';
import { useSwipeable } from 'react-swipeable';
import { 
    getCollectionsForThought, 
    getCollections, 
    addThoughtToCollectionsBatch, 
    removeThoughtFromCollectionsBatch 
} from '../services/collectionService';
import { suggestTagsForThought, suggestNewThemes } from '../services/openAiService';
import { getAllUniqueTags, getAllUniqueIntentions } from '../utils/tagUtils';
import PlusIcon from './common/PlusIcon';
import ActionButton from './common/ActionButton';
import Identicon from './common/Identicon';
import TagsInput from './TagsInput';
import IntentionInput from './common/IntentionInput';
import useTags from '../hooks/useTags';
import CreateCollectionModal from './CreateCollectionModal';

const ThoughtDetail = ({ thoughts, onSave, updateThoughtLocally, isDataLoading = false, deleteThought }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [thought, setThought] = useState(null);
    
    // Add state for copy button icon
    const [showCopyCheck, setShowCopyCheck] = useState(false);
    const [showCommentCopyCheck, setShowCommentCopyCheck] = useState(false);
    
    // Keep a ref to the current UI value to prevent re-renders
    const importanceValueRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveTimeout, setSaveTimeout] = useState(null);
    const [isInsightSaving, setIsInsightSaving] = useState(false);
    const [isCurrentSaving, setIsCurrentSaving] = useState(false);
    
    // State for comments editing
    const [showCommentsEditor, setShowCommentsEditor] = useState(false);
    const [commentsValue, setCommentsValue] = useState('');
    const [isCommentsSaving, setIsCommentsSaving] = useState(false);
    
    // Use both isDataLoading from props and local loading state
    const [isLocalLoading, setIsLocalLoading] = useState(true);
    const isLoading = isDataLoading || isLocalLoading;
    // Fade in animation state
    const [isVisible, setIsVisible] = useState(false);

    // State for collections this thought appears in
    const [collections, setCollections] = useState([]);
    const [collectionsLoading, setCollectionsLoading] = useState(true);
    
    // New state for inline collections management
    const [showCollectionsManager, setShowCollectionsManager] = useState(false);
    const [allCollections, setAllCollections] = useState([]);
    const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
    const [isCollectionsSaving, setIsCollectionsSaving] = useState(false);
    const [collectionSearchTerm, setCollectionSearchTerm] = useState('');
    const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
    
    // State for suggested tags from OpenAI
    const [suggestedTags, setSuggestedTags] = useState([]);
    const [suggestingTags, setSuggestingTags] = useState(false);
    
    // State for selected AI suggested tags
    const [selectedAiTags, setSelectedAiTags] = useState([]);
    const [isSavingTags, setIsSavingTags] = useState(false);
    const [hideAiSuggestions, setHideAiSuggestions] = useState(false);
    const [permanentlyHideAiSuggestions, setPermanentlyHideAiSuggestions] = useState(false);
    
    // State to track which suggestion mode we're in (regular tags or new themes)
    const [suggestionMode, setSuggestionMode] = useState('existingTags'); // 'existingTags' or 'newThemes'

    // Add state for showing tags editor
    const [showTagsEditor, setShowTagsEditor] = useState(false);

    // State for intention editing
    const [showIntentionEditor, setShowIntentionEditor] = useState(false);
    const [intentionValue, setIntentionValue] = useState('');
    const [availableIntentions, setAvailableIntentions] = useState([]);
    const [showIntentionDropdown, setShowIntentionDropdown] = useState(false);
    const [isIntentionSaving, setIsIntentionSaving] = useState(false);

    // Initialize tags manager
    const tagsManager = useTags();

    // Update tags manager when thought changes
    useEffect(() => {
        if (thought?.tags) {
            tagsManager.setTags(thought.tags);
        } else {
            tagsManager.resetTags();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought]); // Only depend on thought changes

    // Update available tags when thoughts change
    useEffect(() => {
        const allTags = getAllUniqueTags(thoughts);
        tagsManager.updateAvailableTags(allTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thoughts]); // Only depend on thoughts changes

    // Update available intentions when thoughts change
    useEffect(() => {
        const allIntentions = getAllUniqueIntentions(thoughts);
        setAvailableIntentions(allIntentions);
    }, [thoughts]);

    // Save tags when they change
    const handleTagsChange = async (newTags) => {
        if (!thought) return;
        
        try {
            const updatedThought = { ...thought, tags: newTags };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update the local thought object
            setThought(prev => ({
                ...prev,
                tags: newTags
            }));
        } catch (error) {
            console.error('Error saving tags:', error);
        }
    };

    // Create a function for back navigation to use in both button and swipe
    const handleBack = () => {
        // Apply exit animation before navigation
        const detailContainer = document.querySelector('.page-content');
        if (detailContainer) {
            detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 180);
        } else {
            navigate('/', { replace: true });
        }
    };

    // Swipe handlers for going back
    const swipeHandlers = useSwipeable({
        onSwipedRight: (eventData) => {
            // Only trigger "go back" if swipe started near the left edge (within 50px)
            if (eventData.initial[0] < 50) {
                handleBack();
            }
        },
        trackMouse: false, // Only detect touch swipes, not mouse
        delta: 50, // Minimum swipe distance required
        preventDefaultTouchmoveEvent: false, // Don't prevent vertical scrolling
    });

    // Update the thought when thoughts array changes or id changes
    useEffect(() => {
        const currentThought = thoughts.find(t => t.id === id);
        setThought(currentThought);
        if (currentThought) {
            // Explicitly set to null if importance is not defined
            importanceValueRef.current = currentThought.importance !== undefined ? currentThought.importance : null;
            
            // Force update the slider UI elements
            updateSliderUI(importanceValueRef.current);
        }
    }, [thoughts, id]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeout) clearTimeout(saveTimeout);
        };
    }, [saveTimeout]);

    // Set loading to false after a short delay to ensure data is ready
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLocalLoading(false);
        }, 100); // Updated to match FieldEditPage's 100ms delay
        return () => clearTimeout(timer);
    }, []);

    // Trigger fade in animation after component mounts and loading is done
    useEffect(() => {
        if (!isLoading && thought) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 10); // Reduced delay to match FieldEditPage
            return () => clearTimeout(timer);
        }
    }, [isLoading, thought]);

    // Get collections this thought belongs to
    const fetchCollections = async () => {
        if (thought) {
            setCollectionsLoading(true);
            try {
                const thoughtCollections = await getCollectionsForThought(thought.id);
                setCollections(thoughtCollections);
            } catch (error) {
                console.error('Error fetching collections:', error);
                setCollections([]);
            } finally {
                setCollectionsLoading(false);
            }
        }
    };

    // Add useEffect to fetch collections when thought changes
    useEffect(() => {
        fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought?.id]); // Only re-fetch when thought ID changes

    // Function to fetch all available collections
    const fetchAllCollections = async () => {
        try {
            const allCollections = await getCollections();
            // Filter out tag collections and default collection
            const filteredCollections = allCollections.filter(collection => 
                !collection.isTagCollection && !collection.isDefault
            );
            setAllCollections(filteredCollections);
            // Set initially selected collections
            setSelectedCollectionIds(collections.map(c => c.id));
        } catch (error) {
            console.error('Error fetching all collections:', error);
            setAllCollections([]);
        }
    };

    // Function to toggle collection selection
    const toggleCollectionSelection = (collectionId) => {
        setSelectedCollectionIds(prev => {
            if (prev.includes(collectionId)) {
                return prev.filter(id => id !== collectionId);
            } else {
                return [...prev, collectionId];
            }
        });
    };

    // Function to handle saving collection changes
    const handleSaveCollections = async () => {
        if (isCollectionsSaving) return;
        
        setIsCollectionsSaving(true);
        try {
            // Get existing collection IDs
            const existingIds = collections.map(c => c.id);
            
            // Handle adds and removes
            const idsToAdd = selectedCollectionIds.filter(id => !existingIds.includes(id));
            const idsToRemove = existingIds.filter(id => !selectedCollectionIds.includes(id));
            
            if (idsToAdd.length > 0) {
                await addThoughtToCollectionsBatch(thought.id, idsToAdd);
            }
            
            if (idsToRemove.length > 0) {
                await removeThoughtFromCollectionsBatch(thought.id, idsToRemove);
            }
            
            // Refresh collections
            await fetchCollections();
            
            // Close the manager
            setShowCollectionsManager(false);
        } catch (error) {
            console.error('Error saving collections:', error);
        } finally {
            setIsCollectionsSaving(false);
        }
    };

    // Function to handle collection creation success
    const handleCollectionCreated = async (collectionName) => {
        setShowCreateCollectionModal(false);
        // Wait for collections to be fetched
        await fetchAllCollections();
        // After fetching, select the newly created collection
        const newCollection = allCollections.find(c => c.name === collectionName);
        if (newCollection) {
            setSelectedCollectionIds(prev => [...prev, newCollection.id]);
        }
    };

    // Get tag suggestions from OpenAI when thought is loaded
    useEffect(() => {
        // Create a stable reference to thought.id to prevent unnecessary refreshes
        // eslint-disable-next-line no-unused-vars
        const thoughtText = thought?.thought;
        
        const getSuggestedTags = async () => {
            // Skip API calls if suggestions are permanently hidden
            if (permanentlyHideAiSuggestions) {
                return;
            }
            
            if (thought && thoughtText) {
                // Count words in the thought text
                const wordCount = thoughtText.trim().split(/\s+/).length;
                
                // Only get suggestions if the thought is between 3 and 50 words
                if (wordCount < 3 || wordCount > 50) {
                    setSuggestedTags([]);
                    return;
                }
                
                setSuggestingTags(true);
                try {
                    // Get all unique tags from all thoughts
                    const allTags = getAllUniqueTags(thoughts);
                    
                    // Get the existing tags from the thought
                    const existingTags = thought.tags || [];
                    
                    // Choose which API function to call based on the suggestion mode
                    let suggestions;
                    if (suggestionMode === 'existingTags') {
                        suggestions = await suggestTagsForThought(thoughtText, allTags, existingTags);
                        // For existingTags mode, only keep tags that ARE in the unique tags list
                        suggestions = suggestions.filter(tag => allTags.includes(tag));
                        console.log('Suggested (filtered to existing):', suggestions);
                    } else {
                        suggestions = await suggestNewThemes(thoughtText, allTags, existingTags);
                        // For newThemes mode, only keep tags that are NOT in the unique tags list
                        suggestions = suggestions.filter(tag => !allTags.includes(tag));
                        console.log('Ideas (filtered to new):', suggestions);
                    }
                    
                    setSuggestedTags(suggestions);
                } catch (error) {
                    console.error('Error getting tag suggestions:', error);
                } finally {
                    setSuggestingTags(false);
                }
            }
        };
        
        getSuggestedTags();
    // Only depend on the essentials to prevent unnecessary refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought?.id, thought?.thought, suggestionMode, permanentlyHideAiSuggestions, thoughts]);

    // Function to manually refresh AI suggestions
    const refreshAiSuggestions = () => {
        // Skip if suggestions are permanently hidden
        if (permanentlyHideAiSuggestions) {
            return;
        }
        
        // Reset state
        setHideAiSuggestions(false);
        setSuggestedTags([]);
        
        // Re-run the suggestions logic
        if (thought && thought.thought) {
            // Count words in the thought text
            const wordCount = thought.thought.trim().split(/\s+/).length;
            
            // Only get suggestions if the thought is between 3 and 50 words
            if (wordCount < 3 || wordCount > 50) {
                return;
            }
            
            setSuggestingTags(true);
            
            // Get all unique tags from all thoughts
            const allTags = getAllUniqueTags(thoughts);
            
            // Get the existing tags from the thought
            const existingTags = thought.tags || [];
            
            // Choose which API function to call based on the suggestion mode
            const apiCall = suggestionMode === 'existingTags' 
                ? suggestTagsForThought(thought.thought, allTags, existingTags)
                : suggestNewThemes(thought.thought, allTags, existingTags);
                
            apiCall
                .then(suggestions => {
                    // Filter based on suggestion mode
                    if (suggestionMode === 'existingTags') {
                        // For existingTags mode, only keep tags that ARE in the unique tags list
                        suggestions = suggestions.filter(tag => allTags.includes(tag));
                        console.log('Suggested (filtered to existing):', suggestions);
                    } else {
                        // For newThemes mode, only keep tags that are NOT in the unique tags list
                        suggestions = suggestions.filter(tag => !allTags.includes(tag));
                        console.log('Ideas (filtered to new):', suggestions);
                    }
                    setSuggestedTags(suggestions);
                })
                .catch(error => {
                    console.error('Error getting refreshed suggestions:', error);
                })
                .finally(() => {
                    setSuggestingTags(false);
                });
        }
    };

    // Toggle between suggestion modes (existing tags or new themes)
    const toggleSuggestionMode = () => {
        // Clear existing suggestions
        setSuggestedTags([]);
        
        // Toggle the mode
        setSuggestionMode(prevMode => prevMode === 'existingTags' ? 'newThemes' : 'existingTags');
        
        // Clear any selected tags
        setSelectedAiTags([]);
    };

    // Function to directly update the UI without a state change
    const updateSliderUI = (value) => {
        // Update "Not set" button
        const notSetButton = document.querySelector('.importance-not-set');
        if (notSetButton) {
            notSetButton.style.fontWeight = value === null ? 'bold' : 'normal';
            notSetButton.style.color = colors.textSecondary;
        }
        
        // Update slider filled portion
        const sliderFill = document.querySelector('.importance-slider-fill');
        if (sliderFill && value !== null) {
            sliderFill.style.width = value === 1 ? '5%' : `${((value - 1) / 4) * 100}%`;
            sliderFill.style.display = 'block';
        } else if (sliderFill) {
            sliderFill.style.display = 'none';
        }
        
        // Update number buttons
        const numberButtons = document.querySelectorAll('.importance-number');
        if (numberButtons) {
            numberButtons.forEach((button, index) => {
                const level = index + 1;
                button.style.color = value === level ? (colors.accent.purple || '#4CAF50') : colors.textSecondary;
            });
        }
    };

    // Auto-save the importance value after a brief delay
    const handleImportanceChange = (value) => {
        // Store the value in ref without causing re-render
        importanceValueRef.current = value;
        
        // Directly update the UI elements
        updateSliderUI(value);
        
        // Clear any existing timeout
        if (saveTimeout) clearTimeout(saveTimeout);
        
        // Set a new timeout to save after 500ms of inactivity
        const timeout = setTimeout(async () => {
            if (!isSaving && value !== thought.importance) {
                setIsSaving(true);
                
                // Update saving indicator without re-rendering
                const savingIndicator = document.querySelector('.importance-saving');
                if (savingIndicator) {
                    savingIndicator.style.display = 'block';
                }
                
                try {
                    const updatedThought = { ...thought, importance: value };
                    
                    // If updateThoughtLocally is available, use it for optimistic updates
                    if (updateThoughtLocally) {
                        // Optimistically update the local state first
                        updateThoughtLocally(updatedThought);
                        
                        // Then save to Firebase (without triggering a full data reload)
                        await onSave(updatedThought, true); // Pass flag to avoid reload
                    } else {
                        // Fallback to regular save that might reload all data
                        await onSave(updatedThought);
                    }
                    
                    // Update the local thought object directly with a minimal update
                    // This change prevents a full re-render cascade
                    setThought(prev => {
                        if (!prev) return updatedThought;
                        return Object.assign({}, prev, { importance: value });
                    });
                } catch (error) {
                    console.error('Error saving importance:', error);
                } finally {
                    setIsSaving(false);
                    
                    // Hide saving indicator without re-rendering
                    if (savingIndicator) {
                        savingIndicator.style.display = 'none';
                    }
                }
            }
        }, 500);
        
        setSaveTimeout(timeout);
    };

    const formattedDate = thought ? formatTimestamp(thought.timestamp) : '';

    const handleEdit = () => {
        // Apply exit animation before navigation
        const detailContainer = document.querySelector('.page-content');
        if (detailContainer) {
            detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => {
                navigate(`/edit/${thought.id}`);
            }, 180); // Slightly shorter than animation duration
        } else {
            navigate(`/edit/${thought.id}`);
        }
    };
    
    // Handle thought deletion
    const handleDeleteThought = () => {
        if (window.confirm("Are you sure you want to delete this thought? This action cannot be undone.")) {
            // Apply exit animation before navigation
            const detailContainer = document.querySelector('.page-content');
            if (detailContainer) {
                detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
                setTimeout(() => {
                    // Delete the thought
                    deleteThought(id);
                    // Navigate back to the main page
                    navigate('/', { replace: true });
                }, 180);
            } else {
                deleteThought(id);
                navigate('/', { replace: true });
            }
        }
    };

    // Toggle a tag in the selected AI tags list
    const toggleAiTagSelection = (tag) => {
        setSelectedAiTags(prev => {
            if (prev.includes(tag)) {
                return prev.filter(t => t !== tag);
            } else {
                return [...prev, tag];
            }
        });
    };
    
    // Clear all selected AI tags
    const clearSelectedAiTags = () => {
        setSelectedAiTags([]);
    };
    
    // Save selected AI tags
    const saveSelectedAiTags = async () => {
        if (selectedAiTags.length === 0) return;
        
        // Hide AI suggestions immediately before saving starts
        setHideAiSuggestions(true);
        setPermanentlyHideAiSuggestions(true);
        setIsSavingTags(true);
        
        try {
            // Prepare new tags list combining existing tags and newly selected AI tags
            const currentTags = thought.tags || [];
            const newTags = [...new Set([...currentTags, ...selectedAiTags])];
            
            // Create a new thought object but preserve the same reference for properties we don't change
            const updatedThought = { ...thought, tags: newTags };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true); // Pass flag to avoid reload
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update only the tags property to avoid re-rendering everything
            setThought(prev => {
                if (!prev) return updatedThought;
                return { 
                    ...prev, 
                    tags: newTags 
                };
            });
            
            // Clear selected tags after saving
            setSelectedAiTags([]);
        } catch (error) {
            console.error('Error saving AI suggested tags:', error);
            // If there's an error, show the AI suggestions again
            setHideAiSuggestions(false);
        } finally {
            setIsSavingTags(false);
        }
    };

    // Add insight toggle handler
    const handleInsightToggle = async () => {
        if (isInsightSaving) return;
        
        setIsInsightSaving(true);
        try {
            // Get the current insight value, defaulting to false if undefined
            const currentInsight = thought.insight || false;
            const updatedThought = { 
                ...thought, 
                insight: !currentInsight 
            };
            
            // Update local state first for immediate feedback
            setThought(prev => ({
                ...prev,
                insight: !currentInsight
            }));
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
        } catch (error) {
            // Revert the local state if there's an error
            setThought(prev => ({
                ...prev,
                insight: thought.insight || false
            }));
            console.error('Error toggling insight:', error);
        } finally {
            setIsInsightSaving(false);
        }
    };

    // Add current toggle handler
    const handleCurrentToggle = async () => {
        if (isCurrentSaving) return;
        
        setIsCurrentSaving(true);
        try {
            // Get the current current value, defaulting to false if undefined
            const currentCurrent = thought.current || false;
            const updatedThought = { 
                ...thought, 
                current: !currentCurrent 
            };
            
            // Update local state first for immediate feedback
            setThought(prev => ({
                ...prev,
                current: !currentCurrent
            }));
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
        } catch (error) {
            // Revert the local state if there's an error
            setThought(prev => ({
                ...prev,
                current: thought.current || false
            }));
            console.error('Error toggling current:', error);
        } finally {
            setIsCurrentSaving(false);
        }
    };

    // Handle starting tag edit
    const handleStartTagEdit = () => {
        tagsManager.setTags(thought.tags || []);
        setShowTagsEditor(true);
    };

    // Handle canceling tag edit
    const handleCancelTagEdit = () => {
        setShowTagsEditor(false);
        tagsManager.resetTags();
    };

    // Handle saving tag changes
    const handleSaveTagChanges = async () => {
        await handleTagsChange(tagsManager.selectedTags);
        setShowTagsEditor(false);
    };

    // Handle starting intention edit
    const handleStartIntentionEdit = () => {
        setIntentionValue(thought.intention || '');
        setShowIntentionEditor(true);
    };

    // Handle canceling intention edit
    const handleCancelIntentionEdit = () => {
        setShowIntentionEditor(false);
        setIntentionValue('');
        setShowIntentionDropdown(false);
    };

    // Handle saving intention changes
    const handleSaveIntentionChanges = async () => {
        if (isIntentionSaving) return;
        setIsIntentionSaving(true);
        
        try {
            const updatedThought = { ...thought, intention: intentionValue.trim() || null };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update the local thought object
            setThought(prev => ({
                ...prev,
                intention: intentionValue.trim() || null
            }));
            
            setShowIntentionEditor(false);
            setShowIntentionDropdown(false);
        } catch (error) {
            console.error('Error saving intention:', error);
        } finally {
            setIsIntentionSaving(false);
        }
    };

    // Handle intention input change
    const handleIntentionInputChange = (value) => {
        setIntentionValue(value);
        setShowIntentionDropdown(true);
    };

    // Handle intention selection from dropdown
    const handleIntentionSelect = (intention) => {
        setIntentionValue(intention);
        setShowIntentionDropdown(false);
    };

    // Handle intention clear
    const handleIntentionClear = () => {
        setIntentionValue('');
        setShowIntentionDropdown(false);
    };

    // Handle intention key press
    const handleIntentionKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveIntentionChanges();
        } else if (e.key === 'Escape') {
            handleCancelIntentionEdit();
        }
    };

    // Handle starting comments edit
    const handleStartCommentsEdit = () => {
        setCommentsValue(thought.comments || '');
        setShowCommentsEditor(true);
    };

    // Handle canceling comments edit
    const handleCancelCommentsEdit = () => {
        setShowCommentsEditor(false);
        setCommentsValue('');
    };

    // Handle saving comments changes
    const handleSaveCommentsChanges = async () => {
        if (isCommentsSaving) return;
        setIsCommentsSaving(true);
        
        try {
            const updatedThought = { ...thought, comments: commentsValue };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Then save to Firebase (without triggering a full data reload)
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update the local thought object
            setThought(prev => ({
                ...prev,
                comments: commentsValue
            }));
            
            setShowCommentsEditor(false);
        } catch (error) {
            console.error('Error saving comments:', error);
        } finally {
            setIsCommentsSaving(false);
        }
    };

    // Get button styles from ComposePage
    const buttonStyles = styles.composePage.desktop;

    if (isLoading) {
        return (
            <div style={{ 
                padding: '24px 8px', 
                textAlign: 'center',
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: 0, // Start invisible for the animation
            }} className="page-content">
                {/* Empty loading state with no message */}
                <style>
                    {`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        
                        @keyframes fadeOut {
                            from { opacity: 1; }
                            to { opacity: 0; }
                        }
                    `}
                </style>
            </div>
        );
    }

    if (!thought) {
        return (
            <div style={{ 
                padding: '24px 8px', 
                textAlign: 'center',
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: 1,
            }} className="page-content">
                <style>
                    {`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        
                        @keyframes fadeOut {
                            from { opacity: 1; }
                            to { opacity: 0; }
                        }
                    `}
                </style>
                <p>Thought not found</p>
                <button 
                    onClick={() => navigate('/')} 
                    style={{ ...styles.button, marginTop: '16px' }}
                >
                    Back to Library
                </button>
            </div>
        );
    }

    return (
        <div 
            {...swipeHandlers}
            style={{ 
                padding: '8px 8px', 
                maxWidth: '1000px', 
                margin: '0 auto',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.3s cubic-bezier(0.2, 0, 0, 1)',
                willChange: 'opacity',
            }}
            className="page-content"
        >
            {/* Define animations */}
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes fadeOut {
                        from { opacity: 1; }
                        to { opacity: 0; }
                    }
                `}
            </style>
            
            {/* Back button and date/edit in same row */}
            <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingLeft: '0px',
            }}>
                {/* Back button (left side) */}
                <button 
                    onClick={handleBack} 
                    style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        marginLeft: '0px',
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                
                {/* Date and edit on right side */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: colors.textSecondary, marginRight: '8px' }}>{formattedDate}</span>
                    <button 
                        onClick={handleEdit}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Main thought content */}
            <div 
                onClick={handleEdit}
                style={{ 
                    cursor: 'pointer', 
                    marginBottom: '24px',
                    position: 'relative',
                    padding: '16px',
                    backgroundColor: colors.surfaceElevated,
                    borderRadius: '12px',
                }}
            >
                <p style={{
                    fontSize: '18px',
                    lineHeight: '1.6',
                    color: colors.textPrimary,
                    whiteSpace: 'pre-wrap',
                    marginTop: 0,
                    marginBottom: 0,
                    fontWeight: '400',
                    paddingRight: '32px',
                }}>
                    {thought.thought}
                </p>
                
                {/* Copy button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the edit action
                        navigator.clipboard.writeText(thought.thought)
                            .then(() => {
                                // Show success checkmark
                                setShowCopyCheck(true);
                                
                                // Reset back to copy icon after a moment
                                setTimeout(() => {
                                    setShowCopyCheck(false);
                                }, 500);
                            })
                            .catch(err => console.error('Failed to copy text: ', err));
                    }}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.7,
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                    title="Copy to clipboard"
                >
                    {showCopyCheck ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="8" y="8" width="12" height="12" rx="2" stroke="white" strokeWidth="2" />
                            <path d="M16 8V6c0-1.10457-.8954-2-2-2H6c-1.10457 0-2 .89543-2 2v8c0 1.1046.89543 2 2 2h2" stroke="white" strokeWidth="2" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Metadata section */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '20px',
                paddingTop: '0px',
            }}>
                {/* Insight Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '5px 0 -8px 0',
                    padding: '0 8px',
                }}>
                    <button
                        onClick={handleInsightToggle}
                        disabled={isInsightSaving}
                        style={{
                            width: '44px',
                            height: '24px',
                            backgroundColor: thought?.insight ? colors.accent.violet : colors.surface,
                            border: 'none',
                            outline: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            padding: 0,
                            boxShadow: `0 0 0 1px ${thought?.insight ? colors.accent.violet : colors.divider}`,
                        }}
                    >
                        <div style={{
                            width: '18px',
                            height: '18px',
                            backgroundColor: thought?.insight ? colors.accent.white : colors.textSecondary,
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '3px',
                            left: thought?.insight ? '22px' : '2px',
                            transition: 'all 0.2s ease',
                        }} />
                    </button>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <label style={{ 
                            fontSize: '14px',
                            color: colors.textSecondary,
                        }}>
                            Insight
                        </label>
                        {isInsightSaving && (
                            <span style={{ 
                                fontSize: '14px',
                                color: colors.textSecondary,
                                opacity: 0.7,
                            }}>
                                Saving...
                            </span>
                        )}
                    </div>
                </div>

                {/* Current Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '2px 0 5px 0',
                    padding: '0 8px',
                }}>
                    <button
                        onClick={handleCurrentToggle}
                        disabled={isCurrentSaving}
                        style={{
                            width: '44px',
                            height: '24px',
                            backgroundColor: thought?.current ? colors.accent.violet : colors.surface,
                            border: 'none',
                            outline: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            padding: 0,
                            boxShadow: `0 0 0 1px ${thought?.current ? colors.accent.violet : colors.divider}`,
                        }}
                    >
                        <div style={{
                            width: '18px',
                            height: '18px',
                            backgroundColor: thought?.current ? colors.accent.white : colors.textSecondary,
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '3px',
                            left: thought?.current ? '22px' : '2px',
                            transition: 'all 0.2s ease',
                        }} />
                    </button>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <label style={{ 
                            fontSize: '14px',
                            color: colors.textSecondary,
                        }}>
                            Current
                        </label>
                        {isCurrentSaving && (
                            <span style={{ 
                                fontSize: '14px',
                                color: colors.textSecondary,
                                opacity: 0.7,
                            }}>
                                Saving...
                            </span>
                        )}
                    </div>
                </div>

                {/* Intentions */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <label style={{ 
                        display: 'block', 
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginBottom: '4px',
                    }}>
                        Intention
                    </label>
                    {showIntentionEditor ? (
                        <div>
                            <IntentionInput
                                value={intentionValue}
                                availableIntentions={availableIntentions}
                                showDropdown={showIntentionDropdown}
                                onInputChange={handleIntentionInputChange}
                                onKeyPress={handleIntentionKeyPress}
                                onFocus={() => setShowIntentionDropdown(true)}
                                onBlur={(e) => {
                                    // Delay closing to check if click was on a dropdown item
                                    const currentTarget = e.currentTarget;
                                    setTimeout(() => {
                                        if (currentTarget && !currentTarget.contains(document.activeElement)) {
                                            setShowIntentionDropdown(false);
                                        }
                                    }, 300);
                                }}
                                onIntentionSelect={handleIntentionSelect}
                                onClear={handleIntentionClear}
                            />
                            {/* Save/Cancel buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '16px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCancelIntentionEdit}
                                    style={{
                                        ...buttonStyles.addButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveIntentionChanges}
                                    disabled={isIntentionSaving}
                                    style={{
                                        ...buttonStyles.saveButton,
                                        cursor: isIntentionSaving ? 'default' : 'pointer',
                                        opacity: isIntentionSaving ? 0.7 : 1,
                                    }}
                                >
                                    {isIntentionSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : thought.intention ? (
                        <div 
                            onClick={handleStartIntentionEdit}
                            style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{
                                color: colors.accent.blue,
                                boxShadow: 'none',
                                fontWeight: '600',
                                fontSize: '14px',
                            }}>
                                {thought.intention}
                            </span>
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <ActionButton
                                onClick={handleStartIntentionEdit}
                                icon={<PlusIcon width={14} height={14} />}
                                text="No intention"
                                color={colors.textSecondary}
                                fontSize={17}
                            />
                        </div>
                    )}
                </div>

                {/* Tags */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <label style={{ 
                        display: 'block', 
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginBottom: '4px',
                    }}>
                        Tags
                    </label>
                    {showTagsEditor ? (
                        <div>
                            <TagsInput
                                tagInput={tagsManager.tagInput}
                                selectedTags={tagsManager.selectedTags}
                                filteredTags={tagsManager.filteredTags}
                                showDropdown={tagsManager.showTagDropdown}
                                onInputChange={tagsManager.handleTagInputChange}
                                onKeyPress={tagsManager.handleTagKeyPress}
                                onFocus={() => tagsManager.setShowTagDropdown(true)}
                                onBlur={(e) => {
                                    // Delay closing to check if click was on a dropdown item
                                    const currentTarget = e.currentTarget;
                                    setTimeout(() => {
                                        // Only close if focus is not within the input or dropdown
                                        if (!currentTarget.contains(document.activeElement)) {
                                            tagsManager.setShowTagDropdown(false);
                                        }
                                    }, 300);
                                }}
                                onTagSelect={(tag) => {
                                    tagsManager.addTag(tag);
                                    // Keep dropdown open after selection
                                    tagsManager.setShowTagDropdown(true);
                                }}
                                onTagRemove={tagsManager.removeTag}
                            />
                            {/* Save/Cancel buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '16px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCancelTagEdit}
                                    style={{
                                        ...buttonStyles.addButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveTagChanges}
                                    style={{
                                        ...buttonStyles.saveButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        thought.tags && thought.tags.length > 0 ? (
                            <div 
                                onClick={handleStartTagEdit}
                                style={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: '8px',
                                    cursor: 'pointer'
                                }}
                            >
                                {thought.tags.map((tag, i) => (
                                    <span key={i} style={{
                                        ...styles.tagPill,
                                        backgroundColor: colors.surface,
                                        color: colors.accent.white,
                                        boxShadow: 'none',
                                        border: `1px solid ${colors.accent.white}20`,
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                    }}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div style={{ 
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <ActionButton
                                    onClick={handleStartTagEdit}
                                    icon={<PlusIcon width={14} height={14} />}
                                    text="No tags"
                                    color={colors.textSecondary}
                                    fontSize={17}
                                />
                            </div>
                        )
                    )}
                </div>

                {/* AI Suggestions Magic Wand - show when suggestions are permanently hidden */}
                {permanentlyHideAiSuggestions && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        margin: '5px 0',
                        padding: '0 8px',
                    }}>
                        <button
                            onClick={() => {
                                setPermanentlyHideAiSuggestions(false);
                                setHideAiSuggestions(false);
                                setSelectedAiTags([]); // Clear selected tags immediately
                                // Refresh suggestions when showing them again
                                refreshAiSuggestions();
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: `${colors.accent.purple}10`,
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                color: colors.accent.purple,
                                cursor: 'pointer',
                                transition: 'all 150ms ease',
                                fontWeight: 'bold',
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill={colors.accent.purple}>
                                <path d="m195-143-51-51 292-292-172-42 186-116-16-219 168 141 202-82-82 203 142 167-218.97-16L529-263l-42-172-292 292Zm21-481-72-72 72-72 72 72-72 72Zm341 177 49-78 92 7-59.31-70.2 34.6-85.8-85.02 35L518-698l7 92-79 48.39 89.35 21.73L557-447Zm139 303-72-72 72-72 72 72-72 72ZM572-573Z"/>
                            </svg>
                            Suggest Tags
                        </button>
                    </div>
                )}

                {/* AI Suggested Tags */}
                {suggestedTags && suggestedTags.length > 0 && !hideAiSuggestions && !permanentlyHideAiSuggestions && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        margin: '5px 0',
                        padding: '0 8px',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '4px',
                        }}>
                            <label style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                color: colors.textSecondary,
                            }}>
                                {/* Switch between icons based on the mode */}
                                {suggestionMode === 'existingTags' ? (
                                    <svg width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative', top: '3px' }}>
                                        <path d="M20 16C11 16 11 32 20 32H48L40 24L48 32L40 40" 
                                            fill="none" 
                                            stroke={colors.accent.purple}
                                            strokeWidth="4" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 16H15" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M10 19H14" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M12 3C8.686 3 6 5.686 6 9C6 10.933 7.025 12.59 8.5 13.535C9.109 13.923 9.5 14.58 9.5 15.293V16H14.5V15.293C14.5 14.58 14.891 13.923 15.5 13.535C16.975 12.59 18 10.933 18 9C18 5.686 15.314 3 12 3Z" 
                                            stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                                
                                {suggestionMode === 'existingTags' ? 'Suggested' : 'Ideas'}
                                
                                {/* Mode toggle buttons */}
                                <div style={{ display: 'flex', marginLeft: '8px' }}>
                                    {/* Lightbulb/Tags icon (toggle to themes/tags) */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSuggestionMode();
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        title={suggestionMode === 'existingTags' ? "Switch to theme suggestions" : "Switch to tag suggestions"}
                                    >
                                        {/* Show the opposite icon of current mode to indicate what we'll switch to */}
                                        {suggestionMode === 'existingTags' ? (
                                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 16H15" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M10 19H14" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>                                                
                                                <path d="M12 3C8.686 3 6 5.686 6 9C6 10.933 7.025 12.59 8.5 13.535C9.109 13.923 9.5 14.58 9.5 15.293V16H14.5V15.293C14.5 14.58 14.891 13.923 15.5 13.535C16.975 12.59 18 10.933 18 9C18 5.686 15.314 3 12 3Z" 
                                                    stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <svg width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"
                                                style={{ position: 'relative', top: '3px' }}>
                                                <path d="M20 16C11 16 11 32 20 32H48L40 24L48 32L40 40" 
                                                    fill="none" 
                                                    stroke={colors.textSecondary}
                                                    strokeWidth="4" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </button>
                                    
                                    {/* Refresh button */}
                                    {!suggestingTags && suggestedTags.length > 0 && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent any parent click handlers
                                                refreshAiSuggestions();
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            title="Refresh suggestions"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.5 8.5C17.8 6.2 15.1 4.5 12 4.5C7.9 4.5 4.5 7.9 4.5 12C4.5 16.1 7.9 19.5 12 19.5C15.1 19.5 17.8 17.8 19.5 15.5" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M15 8.5H19.5V4" stroke={colors.textSecondary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </label>
                            
                            {/* Action buttons for multi-select */}
                            {selectedAiTags.length > 0 && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {/* Cancel button (X) */}
                                    <button
                                        onClick={clearSelectedAiTags}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        title="Clear selection"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18" stroke="#FF5A5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M6 6L18 18" stroke="#FF5A5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    
                                    {/* Add tags button (✓) */}
                                    <button
                                        onClick={saveSelectedAiTags}
                                        disabled={isSavingTags}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: isSavingTags ? 0.5 : 1,
                                        }}
                                        title="Add selected tags"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 12L10 17L20 7" stroke={colors.accent.white} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '8px',
                            padding: '4px 0'
                        }}>
                            {suggestedTags
                                .filter(tag => !thought.tags || !thought.tags.includes(tag))
                                .map((tag, i) => (
                                    <span 
                                        key={i} 
                                        onClick={() => toggleAiTagSelection(tag)}
                                        style={{
                                            backgroundColor: selectedAiTags.includes(tag) 
                                                ? `${colors.accent.purple}30` 
                                                : `${colors.accent.purple}10`,
                                            color: colors.accent.purple,
                                            border: selectedAiTags.includes(tag) 
                                                ? `1px solid ${colors.accent.purple}` 
                                                : 'none',
                                            borderRadius: '16px',
                                            padding: '8px 14px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap',
                                            transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {tag}
                                    </span>
                                ))}
                        </div>
                    
                    </div>
                )}

                {/* Appears In section - Collections */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <label style={{ 
                        display: 'block', 
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginBottom: '4px',
                    }}>
                        Appears In
                    </label>
                    {collectionsLoading ? (
                        <div style={{ 
                            color: colors.textSecondary,
                            fontSize: '14px',
                            padding: '4px 0'
                        }}>
                        </div>
                    ) : showCollectionsManager ? (
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            {/* Search input */}
                            <input
                                type="text"
                                value={collectionSearchTerm}
                                onChange={(e) => setCollectionSearchTerm(e.target.value)}
                                placeholder="Search collections..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    backgroundColor: colors.surfaceElevated,
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: colors.textPrimary,
                                    fontSize: '14px',
                                }}
                            />
                            
                            {/* Collections list */}
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '0px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                            }}>
                                {allCollections
                                    .filter(collection => 
                                        collection.name.toLowerCase().includes(collectionSearchTerm.toLowerCase())
                                    )
                                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                    .map((collection, index) => (
                                        <div 
                                            key={collection.id}
                                            onClick={() => toggleCollectionSelection(collection.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '16px',
                                                backgroundColor: colors.background,
                                                cursor: 'pointer',
                                                borderBottom: index === allCollections.filter(c => 
                                                    c.name.toLowerCase().includes(collectionSearchTerm.toLowerCase())
                                                ).length - 1 ? 'none' : `1px solid ${colors.divider}`,
                                            }}
                                        >
                                            {/* Checkmark moved to left */}
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '10px',
                                                backgroundColor: selectedCollectionIds.includes(collection.id) 
                                                    ? colors.accent.white 
                                                    : 'transparent',
                                                border: selectedCollectionIds.includes(collection.id) 
                                                    ? 'none' 
                                                    : `1px solid ${colors.divider}`,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                            }}>
                                                {selectedCollectionIds.includes(collection.id) && (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 6L9 17L4 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </div>
                                            
                                            <span style={{
                                                fontSize: '15px',
                                                lineHeight: '1.5',
                                                color: colors.textPrimary,
                                            }}>
                                                {collection.name}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                            
                            {/* Action buttons */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '8px',
                            }}>
                                <button
                                    onClick={() => setShowCreateCollectionModal(true)}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: colors.textSecondary,
                                        padding: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <PlusIcon width={14} height={14} />
                                    <span>New Collection</span>
                                </button>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setShowCollectionsManager(false)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: colors.accent.white,
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '8px 16px',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    
                                    <button
                                        onClick={handleSaveCollections}
                                        disabled={isCollectionsSaving}
                                        style={{
                                            backgroundColor: colors.accent.white,
                                            color: 'black',
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '8px 16px',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            cursor: !isCollectionsSaving ? 'pointer' : 'not-allowed',
                                            opacity: !isCollectionsSaving ? 1 : 0.5
                                        }}
                                    >
                                        {isCollectionsSaving ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : collections && collections.length > 0 ? (
                        <div 
                            onClick={() => {
                                fetchAllCollections();
                                setShowCollectionsManager(true);
                            }}
                            style={{ 
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            {collections
                                .filter(collection => !collection.name.startsWith('/')) // Filter out tag collections
                                .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                .map((collection, i) => (
                                    <span key={i} style={{
                                        ...styles.tagPill,
                                        backgroundColor: colors.surface,
                                        color: colors.accent.purple,
                                        boxShadow: 'none',
                                        border: `1px solid ${colors.accent.purple}20`,
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <Identicon seed={collection.id} size={16} marginRight={0} />
                                        {collection.name}
                                    </span>
                                ))}
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <ActionButton
                                onClick={() => {
                                    fetchAllCollections();
                                    setShowCollectionsManager(true);
                                }}
                                icon={<PlusIcon width={14} height={14} />}
                                text="No collections"
                                color={colors.textSecondary}
                                fontSize={17}
                            />
                        </div>
                    )}
                </div>

                {/* Importance - Simple clean slider (now last) - using direct DOM manipulation */}
                <div style={{
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <label style={{ 
                            fontSize: '14px',
                            color: colors.textSecondary,
                            fontWeight: 'bold',
                        }}>
                            Importance
                        </label>
                        <span 
                            className="importance-saving"
                            style={{ 
                                fontSize: '14px',
                                color: colors.textSecondary,
                                opacity: 0.7,
                                fontWeight: 'bold',
                                display: isSaving ? 'block' : 'none',
                            }}
                        >
                            Saving...
                        </span>
                    </div>
                    
                    {/* "Not set" button */}
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                        <button
                            className="importance-not-set"
                            type="button"
                            onClick={() => handleImportanceChange(null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 10px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                fontSize: '15px',
                                color: colors.textSecondary,
                                cursor: 'pointer',
                                transition: 'all 150ms ease',
                                fontWeight: thought.importance === null ? 'bold' : 'normal',
                            }}
                        >
                            Not set
                        </button>
                    </div>
                    
                    {/* Clean simple slider - without +/- signs */}
                    <div style={{
                        margin: '12px 0'
                    }}>
                        {/* Simple slider bar */}
                        <div style={{
                            position: 'relative',
                            height: '4px',
                            width: '100%',
                            backgroundColor: colors.divider,
                            borderRadius: '2px',
                            overflow: 'hidden',
                            cursor: 'pointer'
                        }}
                            onClick={(e) => {
                                // Calculate position as percentage of width
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const percent = x / rect.width;
                                
                                // Convert to scale 1-5
                                const rawValue = 1 + Math.floor(percent * 5);
                                const value = Math.max(1, Math.min(5, rawValue));
                                
                                // Update importance
                                handleImportanceChange(value);
                            }}
                        >
                            {/* Filled portion */}
                            <div 
                                className="importance-slider-fill"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: thought.importance === 1 ? '5%' : `${((thought.importance - 1) / 4) * 100}%`,
                                    backgroundColor: colors.accent.purple || '#4CAF50',
                                    transition: 'width 0.2s ease',
                                    display: thought.importance !== null ? 'block' : 'none'
                                }} 
                            />
                        </div>
                    </div>
                    
                    {/* Slider numbers */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        position: 'relative',
                        marginTop: '8px'
                    }}>
                        {[1, 2, 3, 4, 5].map(level => (
                            <button
                                key={level}
                                className="importance-number"
                                onClick={() => handleImportanceChange(level)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: thought.importance === level ? (colors.accent.purple || '#4CAF50') : colors.textSecondary,
                                }}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>
                
                {/* Comments section */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '14px',
                            color: colors.textSecondary,
                            marginBottom: '4px',
                            fontWeight: 'bold',
                        }}>
                            Comments
                        </label>
                        
                        {thought.comments && !showCommentsEditor && (
                            <button 
                                onClick={handleStartCommentsEdit}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                    
                    {showCommentsEditor ? (
                        <div>
                            <div style={{
                                backgroundColor: colors.surfaceElevated,
                                borderRadius: '12px',
                                marginBottom: '16px',
                                padding: '12px',
                            }}>
                                <textarea
                                    value={commentsValue}
                                    onChange={(e) => setCommentsValue(e.target.value)}
                                    placeholder="Add your comments here..."
                                    style={{
                                        width: '100%',
                                        minHeight: '120px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: colors.textPrimary,
                                        fontSize: '15px',
                                        lineHeight: '1.4',
                                        resize: 'vertical',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                    }}
                                    autoFocus
                                />
                            </div>
                            
                            {/* Save/Cancel buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCancelCommentsEdit}
                                    style={{
                                        ...buttonStyles.addButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveCommentsChanges}
                                    disabled={isCommentsSaving}
                                    style={{
                                        ...buttonStyles.saveButton,
                                        cursor: isCommentsSaving ? 'default' : 'pointer',
                                        opacity: isCommentsSaving ? 0.7 : 1,
                                    }}
                                >
                                    {isCommentsSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : thought.comments ? (
                        <div 
                            onClick={handleStartCommentsEdit}
                            style={{ 
                                cursor: 'pointer',
                                padding: '12px',
                                backgroundColor: colors.surfaceElevated,
                                borderRadius: '12px',
                                fontSize: '15px',
                                color: colors.textPrimary,
                                lineHeight: '1.4',
                                whiteSpace: 'pre-wrap',
                                position: 'relative',
                            }}
                        >
                            <p style={{
                                margin: 0,
                                paddingRight: '32px',
                                whiteSpace: 'pre-wrap',
                            }}>
                                {thought.comments}
                            </p>
                            
                            {/* Copy button for comments */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the edit action
                                    navigator.clipboard.writeText(thought.comments)
                                        .then(() => {
                                            // Show success checkmark
                                            setShowCommentCopyCheck(true);
                                            
                                            // Reset back to copy icon after a moment
                                            setTimeout(() => {
                                                setShowCommentCopyCheck(false);
                                            }, 500);
                                        })
                                        .catch(err => console.error('Failed to copy comment text: ', err));
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0.7,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                title="Copy comment to clipboard"
                            >
                                {showCommentCopyCheck ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="8" y="8" width="12" height="12" rx="2" stroke="white" strokeWidth="2" />
                                        <path d="M16 8V6c0-1.10457-.8954-2-2-2H6c-1.10457 0-2 .89543-2 2v8c0 1.1046.89543 2 2 2h2" stroke="white" strokeWidth="2" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            padding: '12px',
                            backgroundColor: `${colors.surfaceElevated}80`,
                            borderRadius: '12px',
                        }}>
                            <ActionButton
                                onClick={handleStartCommentsEdit}
                                icon={<PlusIcon width={14} height={14} />}
                                text="Add comments"
                                color={colors.textSecondary}
                                fontWeight="bold"
                                fontSize={14}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Button */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center',
                padding: '40px 0 40px 0',
                marginTop: '70px'
            }}>
                <button
                    onClick={handleDeleteThought}
                    style={{
                        backgroundColor: 'transparent',
                        color: 'rgb(230, 80, 71)',
                        border: 'none',
                        padding: '8px 16px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                    Delete thought
                </button>
            </div>

            {/* Create Collection Modal */}
            <CreateCollectionModal 
                isVisible={showCreateCollectionModal}
                onClose={() => setShowCreateCollectionModal(false)}
                onSuccess={handleCollectionCreated}
            />
        </div>
    );
};

export default ThoughtDetail; 